// ============================================================
// ColonieConvertModal.js
// Convert a Trial enrollment to a real package.
// Trial $25 already paid stays as TotalPaid — credited toward new total.
// Add-ons are added separately via ➕ after conversion.
// Portal confirm fires automatically after successful conversion.
// ============================================================

import React, { useState } from 'react';
import { X, RefreshCw, Loader } from 'lucide-react';
import { P, PKG_LABEL } from './ColoniePrintUtils';

const ColonieConvertModal = ({ registration, onClose, onSave, apiCall, showError }) => {
  const [pkg, setPkg]     = useState('Cotisation');
  const [saving, setSaving] = useState(false);

  const trialPaid = parseFloat(registration.TotalPaid || 0);
  const newPkgFee = { Cotisation: P.cotisation, Season3Days: P.season3Days, Monthly3Days: P.monthly3Days, NerdOnly: P.nerdOnly }[pkg] || 0;
  const newBalance = Math.max(0, newPkgFee - trialPaid);

  const handleSave = async () => {
    setSaving(true);
    try {
      const result = await apiCall(`/colonie/enrollments/${registration.EnrollmentID}/convert`, {
        method: 'PUT',
        body: JSON.stringify({ newPackageType: pkg }),
      });
      onSave(result);
    } catch (err) { showError(err.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">

        <div className="flex items-center justify-between p-5 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-t-2xl">
          <div>
            <h2 className="text-lg font-bold text-white flex items-center gap-2">
              <RefreshCw size={18}/>Convert Trial
            </h2>
            <p className="text-indigo-100 text-sm mt-0.5">{registration.ChildName}</p>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white p-1.5"><X size={18}/></button>
        </div>

        <div className="p-5 space-y-5">

          {/* Trial credit info */}
          <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4">
            <p className="text-xs font-semibold text-indigo-700 uppercase mb-2">Trial credit</p>
            <div className="flex justify-between text-sm text-indigo-800">
              <span>Trial fee paid</span>
              <span className="font-bold text-green-700">${trialPaid.toFixed(2)} credited</span>
            </div>
            <p className="text-xs text-indigo-500 mt-1">
              This amount will be deducted from the new package total.
              Add-ons can be added via the ➕ button after conversion.
            </p>
          </div>

          {/* Package selector */}
          <div>
            <p className="text-sm font-semibold text-gray-700 mb-2">New Package <span className="text-red-400">*</span></p>
            <div className="space-y-2">
              {[
                { v: 'Cotisation',   label: 'Cotisation',            sub: 'Full season, one-time',      price: `$${P.cotisation}` },
                { v: 'Season3Days',  label: '3 Days/Week — Season',  sub: '$275 full season, one-time', price: `$${P.season3Days}` },
                { v: 'Monthly3Days', label: 'Monthly',               sub: '$275/month, renewable',      price: `$${P.monthly3Days}/mo` },
                { v: 'NerdOnly',     label: 'Nerd — Standalone',     sub: 'Outside camp, no camp',      price: `$${P.nerdOnly}` },
              ].map(o => (
                <button key={o.v} onClick={() => setPkg(o.v)}
                  className={`w-full flex items-center justify-between p-3 rounded-xl border-2 text-left transition-all ${pkg === o.v ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200 hover:border-gray-300'}`}>
                  <div>
                    <p className={`font-semibold text-sm ${pkg === o.v ? 'text-indigo-800' : 'text-gray-700'}`}>{o.label}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{o.sub}</p>
                  </div>
                  <span className={`font-bold text-sm ${pkg === o.v ? 'text-indigo-700' : 'text-gray-500'}`}>{o.price}</span>
                </button>
              ))}
            </div>
          </div>

          {/* New balance preview */}
          <div className="bg-gray-50 rounded-xl border p-4 space-y-1 text-sm">
            <div className="flex justify-between text-gray-600">
              <span>{PKG_LABEL[pkg] || pkg}</span>
              <span>${newPkgFee.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-green-700">
              <span>Trial credit</span>
              <span>−${trialPaid.toFixed(2)}</span>
            </div>
            <div className="flex justify-between font-bold text-base text-gray-800 pt-2 border-t border-gray-200">
              <span>Balance due after conversion</span>
              <span>${newBalance.toFixed(2)}</span>
            </div>
          </div>

          <p className="text-xs text-gray-400 flex items-center gap-1">
            <span>ℹ</span> Portal confirmation will fire automatically after conversion.
          </p>
        </div>

        <div className="flex gap-3 p-5 pt-0">
          <button onClick={onClose} className="flex-1 py-3 border border-gray-300 rounded-xl font-semibold text-gray-700">Cancel</button>
          <button onClick={handleSave} disabled={saving}
            className="flex-1 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl font-semibold disabled:opacity-50 flex items-center justify-center gap-2">
            {saving ? <Loader size={16} className="animate-spin"/> : <RefreshCw size={16}/>}
            {saving ? 'Converting…' : 'Convert Package'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ColonieConvertModal;
